import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import { listEmployersApi, DpEmployer } from "../api/settingsApi";
import {
  createInssPaymentApi,
  deleteInssPaymentApi,
  listInssPaymentsApi,
  updateInssPaymentApi,
  DpInssPayment,
  DpInssPaymentList,
} from "../api/inssApi";
import {
  INSS_PAYMENT_STATUS_BADGE,
  INSS_PAYMENT_STATUS_LABELS,
  INSS_THIRTEENTH_MONTH,
} from "../api/dpTypes";
import {
  AttachmentDraft,
  EMPTY_ATTACHMENT_DRAFT,
  PaymentAttachmentFields,
  PaymentFileButtons,
  resolveAttachments,
} from "../components/PaymentAttachments";
import RecolhimentosTabs from "../components/RecolhimentosTabs";
import { formatCurrency, formatDateOnly, toDateInputValue } from "../utils/format";
import { DP_PAGE, DP_SETTINGS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";
import { GuideFinanceCell } from "../components/FinanceBridgeBox";

const MONTH_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
  "13º salário",
];

const pad2 = (n: number) => String(n).padStart(2, "0");
const competenceLabel = (year: number, month: number) =>
  month === INSS_THIRTEENTH_MONTH ? `13º/${year}` : `${pad2(month)}/${year}`;

/** Hoje como 'YYYY-MM-DD' no fuso local — é o limite do `<input type="date">`. */
const todayInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

/** O típico é lançar a competência do mês anterior (o DARF vence no dia 20 do mês seguinte). */
const previousCompetence = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-based: o mês anterior em 1–12
  return month === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month };
};

const emptyForm = () => {
  const { year, month } = previousCompetence();
  return {
    year: String(year),
    month: String(month),
    amount: "",
    penaltyAmount: "",
    paidAt: "",
    documentNumber: "",
    notes: "",
  };
};

const EMPTY_LIST: DpInssPaymentList = {
  items: [],
  totals: { amount: "0", penalty: "0", total: "0" },
};

export default function InssPaymentsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i),
    [currentYear]
  );

  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [employersLoaded, setEmployersLoaded] = useState(false);
  const [employerId, setEmployerId] = useState("");
  const [year, setYear] = useState(currentYear);

  const [list, setList] = useState<DpInssPaymentList>(EMPTY_LIST);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState<{ payment?: DpInssPayment } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState<AttachmentDraft>(EMPTY_ATTACHMENT_DRAFT);
  const [removing, setRemoving] = useState<DpInssPayment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const activeEmployers = useMemo(() => employers.filter((e) => e.isActive), [employers]);
  const selectedEmployer = activeEmployers.find((e) => e.id === employerId);

  useEffect(() => {
    if (!token) return;
    listEmployersApi(token)
      .then((rows) => {
        setEmployers(rows);
        // Com um único empregador ativo (a Eco), ele é resolvido sem pedir escolha.
        const firstActive = rows.find((e) => e.isActive);
        if (firstActive) setEmployerId(firstActive.id);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar empregadores."))
      .finally(() => setEmployersLoaded(true));
  }, [token]);

  const load = useCallback(async () => {
    if (!token || !employerId) return;
    setLoading(true);
    try {
      setList(await listInssPaymentsApi(token, { employerId, year }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar recolhimentos.");
    } finally {
      setLoading(false);
    }
  }, [token, employerId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (payment?: DpInssPayment) => {
    setForm(
      payment
        ? {
            year: String(payment.year),
            month: String(payment.month),
            amount: String(Number(payment.amount)),
            penaltyAmount: Number(payment.penaltyAmount) ? String(Number(payment.penaltyAmount)) : "",
            paidAt: toDateInputValue(payment.paidAt),
            documentNumber: payment.documentNumber ?? "",
            notes: payment.notes ?? "",
          }
        : emptyForm()
    );
    setFiles(EMPTY_ATTACHMENT_DRAFT);
    setModal({ payment });
  };

  const liveTotal = (Number(form.amount) || 0) + (Number(form.penaltyAmount) || 0);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !modal) return;

    const amount = Number(form.amount);
    const penaltyAmount = form.penaltyAmount.trim() === "" ? 0 : Number(form.penaltyAmount);
    const documentNumber = form.documentNumber.replace(/\s/g, "");
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Informe o valor do DARF (maior que zero).");
      return;
    }
    if (!Number.isFinite(penaltyAmount) || penaltyAmount < 0) {
      toast.error("Multa e juros não podem ser negativos.");
      return;
    }
    if (documentNumber && !/^\d{1,20}$/.test(documentNumber)) {
      toast.error("O número do DARF deve ter só dígitos (até 20).");
      return;
    }

    const common = {
      amount,
      penaltyAmount,
      paidAt: form.paidAt || null,
      documentNumber: documentNumber || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      const attachments = await resolveAttachments(token, "inss-payments", files);
      if (modal.payment) {
        const saved = await updateInssPaymentApi(token, modal.payment.id, { ...common, ...attachments });
        toast.success("Recolhimento atualizado.");
        if (saved?.financeiroWarning) toast.warning(saved.financeiroWarning);
      } else {
        const saved = await createInssPaymentApi(token, {
          ...common,
          ...attachments,
          employerId,
          year: Number(form.year),
          month: Number(form.month),
        });
        toast.success("Competência lançada.");
        if (saved?.financeiroWarning) toast.warning(saved.financeiroWarning);
      }
      setModal(null);
      // Lançou em outro ano: acompanha o filtro, para o registro aparecer na tabela.
      if (!modal.payment && Number(form.year) !== year) setYear(Number(form.year));
      else await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar recolhimento.");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!token || !removing) return;
    setDeleting(true);
    try {
      await deleteInssPaymentApi(token, removing.id);
      toast.success("Lançamento excluído.");
      setRemoving(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir lançamento.");
    } finally {
      setDeleting(false);
    }
  };

  if (!token) return null;

  const noEmployer = employersLoaded && activeEmployers.length === 0;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Recolhimentos" }]}
            title="Recolhimentos de INSS"
            subtitle="Controle mensal do DARF da contribuição previdenciária (DCTFWeb): quanto foi recolhido e quando."
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <RecolhimentosTabs />

          {noEmployer ? (
            <div className="dpNotice dpNotice--warning">
              Nenhum empregador ativo cadastrado. Cadastre o CNPJ em{" "}
              <button
                type="button"
                className="dpBtn dpBtn--secondary dpBtn--small"
                onClick={() => navigate(DP_SETTINGS_PAGE())}
              >
                Cadastros
              </button>{" "}
              antes de lançar recolhimentos.
            </div>
          ) : (
            <>
              <div className="dpControls">
                <select
                  className="dpSelect"
                  aria-label="Ano"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                {activeEmployers.length > 1 ? (
                  <select
                    className="dpSelect"
                    aria-label="Empregador"
                    value={employerId}
                    onChange={(e) => setEmployerId(e.target.value)}
                  >
                    {activeEmployers.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.tradeName || e.legalName}
                      </option>
                    ))}
                  </select>
                ) : null}

                <button
                  className="dpBtn dpBtn--primary"
                  onClick={() => openModal()}
                  disabled={!employerId}
                >
                  Lançar competência
                </button>
              </div>

              <p className="dpNotice">
                O vencimento é o dia 20 do mês seguinte à competência; o do 13º salário é 20/12 do
                próprio ano. Não há ajuste para fim de semana ou feriado (a regra do INSS é
                antecipar para o dia útil anterior). Sem data de pagamento, a competência fica
                pendente até o vencimento e atrasada depois dele.
              </p>

              {loading ? (
                <div className="dpEmpty">Carregando...</div>
              ) : (
                <div className="dpTableWrapper">
                  <table className="dpTable dpTable--inss">
                    <thead>
                      <tr>
                        <th>Competência</th>
                        <th>Valor</th>
                        <th>Multa/juros</th>
                        <th>Total</th>
                        <th>Vencimento</th>
                        <th>Pago em</th>
                        <th>Nº do DARF</th>
                        <th>Situação</th>
                        <th>Financeiro</th>
                        <th style={{ width: 260 }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.items.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="dpEmpty">
                            Nenhuma competência lançada em {year}.
                          </td>
                        </tr>
                      ) : (
                        list.items.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <strong>{competenceLabel(p.year, p.month)}</strong>
                              {p.notes ? <div className="dpFormHint">{p.notes}</div> : null}
                            </td>
                            <td>{formatCurrency(p.amount)}</td>
                            <td>{formatCurrency(p.penaltyAmount)}</td>
                            <td>
                              <strong>{formatCurrency(p.totalAmount)}</strong>
                            </td>
                            <td>{formatDateOnly(p.dueDate)}</td>
                            <td>{formatDateOnly(p.paidAt)}</td>
                            <td>{p.documentNumber || "—"}</td>
                            <td>
                              <span className={`dpBadge ${INSS_PAYMENT_STATUS_BADGE[p.status]}`}>
                                {INSS_PAYMENT_STATUS_LABELS[p.status]}
                              </span>
                            </td>
                            <td>
                              <GuideFinanceCell finance={p.financeiro} guideTotal={p.totalAmount} warning={p.financeiroWarning} />
                            </td>
                            <td>
                              <div className="dpTable__actions">
                                <PaymentFileButtons token={token} area="inss-payments" id={p.id} files={p} />
                                <button
                                  className="dpBtn dpBtn--secondary dpBtn--small"
                                  onClick={() => openModal(p)}
                                >
                                  Editar
                                </button>
                                <button
                                  className="dpBtn dpBtn--danger dpBtn--small"
                                  onClick={() => setRemoving(p)}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {list.items.length > 0 ? (
                      <tfoot>
                        <tr>
                          <td>Total {year}</td>
                          <td>{formatCurrency(list.totals.amount)}</td>
                          <td>{formatCurrency(list.totals.penalty)}</td>
                          <td>{formatCurrency(list.totals.total)}</td>
                          <td colSpan={6}></td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ── Modal: lançar / editar competência ─────────────────────────────── */}
      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {modal.payment
                  ? `Editar competência ${competenceLabel(modal.payment.year, modal.payment.month)}`
                  : "Lançar competência"}
              </h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={save}>
              <div className="dpModal__body">
                {activeEmployers.length > 1 && selectedEmployer ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    Empregador: {selectedEmployer.tradeName || selectedEmployer.legalName}
                  </p>
                ) : null}

                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="inssMonth">Competência *</label>
                    <select
                      id="inssMonth"
                      className="dpSelect"
                      value={form.month}
                      onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
                      disabled={saving || !!modal.payment}
                    >
                      {MONTH_LABELS.map((label, i) => (
                        <option key={label} value={i + 1}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="dpFormGroup">
                    <label htmlFor="inssYear">Ano *</label>
                    <select
                      id="inssYear"
                      className="dpSelect"
                      value={form.year}
                      onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                      disabled={saving || !!modal.payment}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {modal.payment ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    A competência não muda. Se foi lançada errada, exclua e lance de novo.
                  </p>
                ) : Number(form.month) === INSS_THIRTEENTH_MONTH ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    O 13º vence em 20/12 e só pode ser lançado a partir de dezembro do próprio ano.
                  </p>
                ) : null}

                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="inssAmount">Valor do DARF (R$) *</label>
                    <input
                      id="inssAmount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="dpInput"
                      value={form.amount}
                      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                      disabled={saving}
                    />
                  </div>

                  <div className="dpFormGroup">
                    <label htmlFor="inssPenalty">Multa e juros (R$)</label>
                    <input
                      id="inssPenalty"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      placeholder="0,00"
                      value={form.penaltyAmount}
                      onChange={(e) => setForm((f) => ({ ...f, penaltyAmount: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Só se o DARF foi pago em atraso.</span>
                  </div>
                </div>

                <div className="dpNotice" style={{ margin: 0 }}>
                  Total pago: <strong>{formatCurrency(liveTotal)}</strong>
                </div>

                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="inssPaidAt">Data do pagamento</label>
                    <input
                      id="inssPaidAt"
                      type="date"
                      className="dpInput"
                      max={todayInputValue()}
                      value={form.paidAt}
                      onChange={(e) => setForm((f) => ({ ...f, paidAt: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Deixe vazio se ainda não pagou.</span>
                  </div>

                  <div className="dpFormGroup">
                    <label htmlFor="inssDocument">Nº do DARF</label>
                    <input
                      id="inssDocument"
                      type="text"
                      inputMode="numeric"
                      maxLength={20}
                      className="dpInput"
                      value={form.documentNumber}
                      onChange={(e) => setForm((f) => ({ ...f, documentNumber: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Opcional. Só dígitos.</span>
                  </div>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="inssNotes">Observação</label>
                  <textarea
                    id="inssNotes"
                    className="dpTextarea"
                    maxLength={1000}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <PaymentAttachmentFields idPrefix="inssFile" current={modal.payment} draft={files} onChange={setFiles} disabled={saving} />
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={!!removing}
        title="Excluir lançamento"
        message={removing ? `Excluir o lançamento da competência ${competenceLabel(removing.year, removing.month)}? Os anexos deixam de aparecer.` : ""}
        confirmLabel="Excluir"
        tone="danger"
        busy={deleting}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
